'use client';

import React from 'react';
import { GameInfoModalWrapper } from '../subcomponents/game-info-modal-wrapper';
import { getText } from '@/utils/text/text';
import { GameBadgeImage } from '@/app/components/game-badge-image/game-badge-image';
import { GameUrl } from '@/app/components/game/subcomponents/game-url/game-url';
import { useStore } from '@/store/store';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { SocialTagBadge } from '@/app/components/badge/layouts/social-tag/social-tag-badge';
import { toGameViewModel } from '@/logic/games/to-game-view-model';
import { POPUPS } from '@/app/components/popup/constants';
import type { Game as GameType } from '@/types/games';
import { FavoritesButton } from '@/app/elements/favorites-button/favorites-button';
import { FAVORITES_VARIANT } from '@/app/elements/favorites-button/favorites-button.constants';
import { useGlobalContext } from '@/store/contexts/global-context';

export const GameInfoModalWeb: React.FC = () => {
    const { queue, closePopup } = useStore((s) => s.popup);
    const { region, isSiteWideJackpotEnabled } = useGlobalContext();

    // Find GAME_INFO in queue (supports overlay scenarios where it's not the active popup)
    const gameInfoPopup = queue.find((item) => item.type === POPUPS.GAME_INFO);
    const isInQueue = !!gameInfoPopup;
    const gameData = gameInfoPopup?.params;

    if (!gameData) {
        return null;
    }

    const { data } = gameData as GameType;
    const game = toGameViewModel(gameData as GameType, { region, isMobile: false });

    const [category, bet] = (game.description ?? '').split(' | ');
    const gameName = game.name ?? '';
    const backgroundImgUrl = game.backgroundUrl;
    const isSiteWideJackpot = (game?.isSiteWideJackpot ?? false) && isSiteWideJackpotEnabled;

    return (
        <GameInfoModalWrapper show={isInQueue} onClose={closePopup} backgroundImgUrl={backgroundImgUrl}>
            <div
                className="game-info-modal"
                data-testid="game-info-modal"
                data-t-module="game_info_modal"
                data-t-gamename={gameName}
                data-t-zonename="game_info_modal"
                data-t-gameuid={gameData.uid}
                data-t-activecount={
                    typeof data?.unique_player_count === 'number' ? String(data.unique_player_count) : ''
                }
                data-t-categoryuid="game_info_modal"
                data-t-dynamicordering={gameInfoPopup?.params?.isDynamicOrderingEnabled}
                data-t-isswj={isSiteWideJackpot}>
                <div className="game-info-modal__tile">
                    <GameBadgeImage
                        logoUrl={game.logoUrl}
                        backgroundUrl={game.backgroundUrl}
                        customBackgroundColor={game.customBackgroundColor}
                        name={game.name}
                        label={game.label}
                        feedsCode={game.feedsCode}
                        isSiteWideJackpot={isSiteWideJackpot}
                    />
                </div>
                <div className="game-info-modal__details">
                    <span className="game-info-modal__details-title">{gameName}</span>
                    <div className="game-info-modal__details-info">
                        <div className="game-info-modal__details-info-wrapper">
                            <span className="game-info-modal__details-info-value">{bet}</span>
                            <span className="game-info-modal__details-info-label">
                                {getText({ text: 'common.minMaxBet' })}
                            </span>
                        </div>
                        <div className="game-info-modal__details-info-wrapper">
                            <span className="game-info-modal__details-info-value">{category}</span>
                            <span className="game-info-modal__details-info-label">
                                {getText({ text: 'common.category' })}
                            </span>
                        </div>
                    </div>
                </div>
                <SocialTagBadge uniquePlayersCount={data?.unique_player_count} state={data?.badge_state} />
                <div className="game-info-modal__cta">
                    <FavoritesButton
                        uid={(gameData as GameType).uid}
                        variant={FAVORITES_VARIANT.OUTLINED}
                        isRenderRequired
                    />
                    <div className="game-info-modal__cta-buttons">
                        <GameUrl launcherUrlProps={game.launcherUrlProps} data-testid="gip-cta-play-now-button">
                            <span className="game-info-modal__cta-button play-now-btn game-info-modal__cta-button--primary">
                                {getText({ text: 'common.playNow' })}
                            </span>
                        </GameUrl>
                    </div>
                </div>
            </div>
        </GameInfoModalWrapper>
    );
};
